import { STAGE_COUNT } from '../../shared/protocol';
import type { PlatformSpec } from '../world/CollisionWorld';

export interface CrateSpec {
  x: number;
  z: number;
  size: number;
  color: number;
  /** Tinggi crate (default 1.2). Crate jembatan sungai lebih pendek (0.8)
   *  supaya mudah dinaiki dengan lompatan 1.2. */
  height?: number;
}

export interface WaterDef {
  x: number;
  z: number;
  w: number;
  d: number;
  /** Ketinggian permukaan air (visual, di atas lantai 0). */
  y: number;
  /** Kedalaman dasar parit cekung (opsional). Bila ada, lantai arena
   *  dibelah dan tanah di area air turun ke `bed`; crate yang didorong
   *  masuk terparkir rata dengan bibir (bed + height = 0). */
  bed?: number;
}

export interface FinishDef {
  x: number;
  z: number;
  r: number;
  needCrate: boolean;
}

/** Dinding gate solid antar-bagian stage (collider nyata setinggi 6 m —
 *  tak bisa dilompati; baru terbuka setelah checkpoint terpenuhi). */
export interface GateDef {
  z: number;
  /** Gate terbuka saat checkpoint ini terpenuhi. */
  require: 'c1' | 'c2' | 'c3';
}

/** Zona validasi checkpoint (state, bukan sekadar posisi). */
export interface CheckpointDef {
  c1?: {
    /** Indeks crate (di def.crates) yang harus terparkir presisi di gap. */
    crates: number[];
    /** Target parkir [x, z, radius toleransi] per crate. */
    targets: Array<[number, number, number]>;
    /** Pemain harus grounded di bank jauh (z > ini) juga. */
    bankZ: number;
  };
  c2?: {
    zFrom: number;
    zTo: number;
    xMax: number;
    /** Kaki pemain minimal (mencegah trigger dari strip bawah). */
    yMin?: number;
  };
  c3?: {
    /** Garis sukses lari (z). */
    lineZ: number;
    /** Z mulai dihitung (kondisi mulai lari). */
    startZ: number;
    /** Z reset saat gagal. */
    resetZ: number;
    /** Indeks crate yang wajib ikut melewati garis (satu crate full-stage). */
    requireCrate?: number;
  };
}

export type DecoKind = 'treeA' | 'treeB' | 'treeC' | 'grassA' | 'grassB' | 'rock' | 'flower';

/** Dekorasi eksplisit (statis, tanpa collider: lihat prefabs.ts). */
export interface DecoSpec {
  kind: DecoKind;
  x: number;
  z: number;
  /** Skala seragam (variasi kedalaman). */
  s?: number;
  /** Rotasi yaw (rad). */
  a?: number;
}

export interface StageDef {
  name: string;
  desc: string;
  boxes: Array<[x: number, y: number, z: number, w: number, h: number, d: number, color: number]>;
  ramps: Array<[x: number, z: number, w: number, d: number, lowY: number, highY: number, color: number]>;
  crates: CrateSpec[];
  /** 4 titik spawn [x, z] dekat start. */
  spawns: Array<[number, number]>;
  /** Finish tunggal (Stage 1-8). Stage multifinish memakai `finishes`. */
  finish: FinishDef;
  /** Multi-finish: beberapa pad, semua harus terisi crate. */
  finishes?: FinishDef[];
  /** Platform/jembatan bergerak (pendukung push+jump+run bertahap). */
  platforms?: PlatformSpec[];
  /** Tema palet pastel (lihat world/art/palette.ts). */
  theme?: string;
  /** Setengah ukuran kotak akrilik arena. */
  arena?: { half: number };
  /** Pohon/rumput/batu/bunga di tepi jalur (visual murni). */
  deco?: DecoSpec[];
  /** Crate tambahan khusus multiplayer (0 untuk 1P, 1 untuk 2P, semua untuk 3-4P). */
  mpExtraCrates?: CrateSpec[];
  /** Zona sungai: jalan terhalang air; letakkan crate di dalamnya sebagai pijakan. */
  waters?: WaterDef[];
  /** Lebar gate/corridor (default 3.8). */
  corridorW?: number;
  /** Gate solid antar bagian (lihat GateDef). */
  gates?: GateDef[];
  /** Zona/syarat checkpoint (lihat CheckpointDef). */
  checkpoints?: CheckpointDef;
  /** Finish butuh crate membatu di pad selama N detik (anti "lewat saja"). */
  finishSettle?: { speedMax: number; time: number };
  /** Indeks crate yang harus masuk pad finish (default: crate mana pun). */
  finishCrate?: number;
}

const STEP = 0x88c0d0;
const PLAT = 0x5e81ac;
const RAMP = 0x8fbcbb;

/** 10 tantangan grab: seret crate ke pad finish. */
export const STAGES: StageDef[] = [
  {
    name: 'Langkah Pertama',
    desc: 'Satu crate menemanimu seluruh lembah: dorong naik, panjat anak tangga, lari, capai finish.',
    boxes: [
      // Bahu lembah (canyon): solid 6 m — tak bisa dipanjat/dilompati,
      // menutup semua bypass di luar koridor 3.6 m.
      [-10.9, 3, 0, 18.2, 6, 40, 0x8fa3b3],
      [10.9, 3, 0, 18.2, 6, 40, 0x8fa3b3],
      // Pijakan datar tengah ramp S1 (atas y 0.6, z -7..-6: RATA dengan
      // ujung kedua ramp — tanpa gap/bibir, crate lewat mulus).
      [0, 0.3, -6.5, 3.6, 0.6, 1.0, STEP],
      // Deck atas S1 (atas y 1.2, z -3.5..0.5).
      [0, 0.6, -1.5, 3.6, 1.2, 4.0, STEP],
      // Anak tangga S2 SELEBAR koridor penuh (3.6, tanpa strip samping):
      // strip 0.8 m < crate 1.4 m = jebakan kejepit. Naik 0.5 per trap;
      // crate ikut naik, pemain melompat.
      [0, 0.25, 2, 3.6, 0.5, 2, STEP],
      [0, 0.5, 4, 3.6, 1.0, 2, STEP],
      [0, 0.75, 6, 3.6, 1.5, 2, STEP],
      // Deck lari + finish S3/S4 (atas y 1.5, z 7.0..20.0: FULL sampai
      // tembok arena — tak ada strip buntu di ujung yang tak bisa dinaiki).
      [0, 0.75, 13.5, 3.6, 1.5, 13.0, STEP],
    ],
    // Ramp S1 dua tahap (landai 0.3/0.24, terbukti bisa didorong crate).
    // Rantai TANPA sela: lantai -> rampA -> rest -> rampB -> deck,
    // tiap sambungan flush (bibir naik 0) supaya crate tak terjebak.
    ramps: [
      [0, -8, 3.6, 2, 0, 0.6, RAMP],
      [0, -4.75, 3.6, 2.5, 0.6, 1.2, RAMP],
    ],
    crates: [{ x: 0, z: -9, size: 1.4, height: 0.8, color: 0xd08770 }],
    spawns: [[-0.8, -10.5], [0.8, -10.5], [-0.8, -11.5], [0.8, -11.5]],
    finish: { x: 0, z: 18.6, r: 0.95, needCrate: true },
    mpExtraCrates: [],
    platforms: [
      // Dinding pengejar S3: istirahat DI BELAKANG Gate2 (tak menghalangi
      // jalan), sapuan +Z saat pemain lewat garis start. Tak pernah diam
      // di tengah jalur (itu jalan buntu).
      {
        x: 0,
        z: 8.2,
        w: 3.6,
        d: 0.5,
        h: 1.6,
        y: 3.1,
        axis: 'z',
        span: 0,
        period: 1,
        mode: 'chase',
        chaseEndZ: 14.8,
        chaseSpeed: 1.6,
      },
    ],
    corridorW: 3.9,
    gates: [
      { z: 0.5, require: 'c1' },
      { z: 7.6, require: 'c2' },
      { z: 15.4, require: 'c3' },
    ],
    checkpoints: {
      c1: {
        crates: [0],
        targets: [[0, -2.0, 0.8]],
        bankZ: -0.5,
      },
      c2: { zFrom: 5.4, zTo: 7.0, xMax: 1.0, yMin: 1.0 },
      // Garis 14.2 (BUKAN 14.6): crate yang terjepit rata di muka gate
      // (tengah 14.4) tetap lolos — 14.6 = softlock 5 cm.
      c3: { lineZ: 14.2, startZ: 9.2, resetZ: 8.8, requireCrate: 0 },
    },
    finishCrate: 0,
    finishSettle: { speedMax: 0.15, time: 0.5 },
    theme: 'meadow',
    arena: { half: 20 },
    waters: [],
    deco: [
      { kind: 'flower', x: -1.2, z: -11.6 },
      { kind: 'flower', x: 1.2, z: -11.8 },
      { kind: 'rock', x: -1.4, z: -9.2, s: 1.1 },
      { kind: 'flower', x: 1.4, z: 2 },
      { kind: 'flower', x: -1.4, z: 6 },
    ],
  },
  {
    name: 'Naik Tangga',
    desc: 'Tarik crate naik tangga lalu belok ke panggung.',
    boxes: [
      [0, 0.25, 2, 3, 0.5, 2, STEP],
      [0, 0.5, 4, 3, 1.0, 2, STEP],
      [2.5, 0.5, 4, 3, 1.0, 3, STEP],
    ],
    ramps: [],
    crates: [{ x: 0, z: 0, size: 1.0, color: 0xd08770 }],
    spawns: [[-1.5, -3], [1.5, -3], [-1.5, -5], [1.5, -5]],
    finish: { x: 2.5, z: 4, r: 1.0, needCrate: true },
    mpExtraCrates: [
      { x: -4, z: -2, size: 1.0, color: 0xd08770 },
      { x: 4, z: -2, size: 1.0, color: 0xd08770 },
    ],
    theme: 'lavender',
    arena: { half: 10 },
    deco: [
      { kind: 'treeA', x: -8.5, z: -8.5, s: 1.2 },
      { kind: 'treeB', x: 8.5, z: -8.5, s: 1.2 },
      { kind: 'treeB', x: -8.5, z: 8.5, s: 1.1 },
      { kind: 'treeA', x: 8.5, z: 8.5, s: 1.3 },
      { kind: 'treeC', x: -8.2, z: -9.1, s: 1.15 },
      { kind: 'treeC', x: 8.2, z: -9.1, s: 1.1 },
      { kind: 'treeA', x: -8.6, z: -1, s: 1.05 },
      { kind: 'treeB', x: 8.6, z: 1, s: 1.1 },
      { kind: 'treeB', x: 0, z: 9.0, s: 1.3 },
      { kind: 'flower', x: -3.4, z: 5.6 },
      { kind: 'flower', x: 4.8, z: 6.4 },
      { kind: 'flower', x: -3.2, z: -0.8 },
      { kind: 'flower', x: 3.6, z: -6.6 },
      { kind: 'rock', x: 7.9, z: -7.6, s: 1.25 },
      { kind: 'rock', x: -7.8, z: 7.7, s: 1.15 },
    ],
  },
  {
    name: 'Tanjakan Panjang',
    desc: 'Selipkan crate menanjak di antara tiang.',
    boxes: [
      [-1, 1.9, -2, 0.5, 2.0, 0.5, PLAT],
      [1, 2.65, 0.5, 0.5, 2.0, 0.5, PLAT],
    ],
    ramps: [[0, 0, 4, 10, 0, 3, RAMP]],
    crates: [{ x: 0, z: -6, size: 1.0, color: 0xebcb8b }],
    spawns: [[-1.5, -7], [1.5, -7], [-1.5, -9], [1.5, -9]],
    finish: { x: 0, z: 4.2, r: 1.1, needCrate: true },
    mpExtraCrates: [
      { x: -5, z: -7, size: 1.0, color: 0xebcb8b },
      { x: 5, z: -7, size: 1.0, color: 0xebcb8b },
    ],
    theme: 'dune',
    arena: { half: 13 },
    deco: [
      { kind: 'treeC', x: -6, z: -8, s: 1.15 },
      { kind: 'treeC', x: 6, z: -8, s: 1.15 },
      { kind: 'treeC', x: -6, z: 0, s: 1.1 },
      { kind: 'treeC', x: 6, z: 0, s: 1.1 },
      { kind: 'treeC', x: -6, z: 6, s: 1.1 },
      { kind: 'treeC', x: 6, z: 6, s: 1.1 },
      { kind: 'treeA', x: -11.5, z: -11.5, s: 1.25 },
      { kind: 'treeB', x: 11.5, z: -11.5, s: 1.2 },
      { kind: 'treeB', x: -11.5, z: 11.5, s: 1.2 },
      { kind: 'treeA', x: 11.5, z: 11.5, s: 1.3 },
      { kind: 'flower', x: -4.6, z: -9.4 },
      { kind: 'flower', x: 4.6, z: -9.4 },
      { kind: 'flower', x: -4.6, z: 7.8 },
      { kind: 'flower', x: 4.6, z: 7.8 },
      { kind: 'rock', x: 9.5, z: 3.5, s: 1.3 },
      { kind: 'rock', x: -9.5, z: -6, s: 1.1 },
    ],
  },
  {
    name: 'Slalom Tiang',
    desc: 'Selipkan crate menyusuri lorong tiang.',
    boxes: [
      [-1.4, 1.0, -1, 0.5, 2.0, 0.5, PLAT],
      [1.4, 1.0, 1, 0.5, 2.0, 0.5, PLAT],
      [-1.4, 1.0, 3, 0.5, 2.0, 0.5, PLAT],
      [1.4, 1.0, 5, 0.5, 2.0, 0.5, PLAT],
      [-2.8, 0.6, -1, 0.5, 1.2, 12, PLAT],
      [2.8, 0.6, -1, 0.5, 1.2, 12, PLAT],
    ],
    ramps: [],
    crates: [{ x: 0, z: -3, size: 1.0, color: 0xb48ead }],
    spawns: [[-1.5, -8.5], [1.5, -8.5], [-1.5, -10.5], [1.5, -10.5]],
    finish: { x: 0, z: 7.5, r: 1.1, needCrate: true },
    mpExtraCrates: [
      { x: -5, z: -4, size: 1.0, color: 0xb48ead },
      { x: 5, z: -4, size: 1.0, color: 0xb48ead },
    ],
    theme: 'pole',
    arena: { half: 12 },
    deco: [
      { kind: 'treeB', x: -4.5, z: 8.5, s: 1.15 },
      { kind: 'treeB', x: 4.5, z: 8.5, s: 1.15 },
      { kind: 'treeA', x: 0, z: 9.4, s: 1.3 },
      { kind: 'treeC', x: -9, z: -10.5, s: 1.1 },
      { kind: 'treeC', x: 9, z: -10.5, s: 1.1 },
      { kind: 'treeC', x: -4.5, z: -10.4, s: 1.1 },
      { kind: 'treeC', x: 4.5, z: -10.4, s: 1.1 },
      { kind: 'treeA', x: -10.8, z: -10.8, s: 1.25 },
      { kind: 'treeB', x: 10.8, z: -10.8, s: 1.2 },
      { kind: 'treeB', x: -10.8, z: 10.8, s: 1.2 },
      { kind: 'treeA', x: 10.8, z: 10.8, s: 1.3 },
      { kind: 'treeB', x: 10.6, z: -2, s: 1.05 },
      { kind: 'treeA', x: -10.6, z: 3, s: 1.1 },
      { kind: 'flower', x: -4.5, z: -1 },
      { kind: 'flower', x: 4.5, z: -1 },
      { kind: 'flower', x: -4.5, z: 5 },
      { kind: 'flower', x: 4.5, z: 5 },
      { kind: 'rock', x: 7.6, z: 7.6, s: 1.2 },
      { kind: 'rock', x: -7.6, z: 7.6, s: 1.25 },
    ],
  },
  {
    name: 'Atas Panggung',
    desc: 'Naik ramp, seberangi panggung, turun ke teras.',
    boxes: [[0, 1.0, 4, 4, 2.0, 4, PLAT], [3.5, 0.5, 4, 3, 1.0, 4, PLAT]],
    ramps: [[0, -1, 3, 6, 0, 2, RAMP]],
    crates: [{ x: 0, z: -5, size: 1.0, color: 0x8fbcbb }],
    spawns: [[-1.5, -6], [1.5, -6], [-1.5, -8], [1.5, -8]],
    finish: { x: 3.5, z: 4, r: 1.2, needCrate: true },
    mpExtraCrates: [
      { x: -5, z: -4, size: 1.0, color: 0x8fbcbb },
      { x: 5, z: -4, size: 1.0, color: 0x8fbcbb },
    ],
    theme: 'stagehall',
    arena: { half: 13 },
    deco: [
      { kind: 'treeA', x: -11.6, z: -11.6, s: 1.25 },
      { kind: 'treeB', x: 11.6, z: -11.6, s: 1.2 },
      { kind: 'treeB', x: -11.6, z: 11.6, s: 1.2 },
      { kind: 'treeA', x: 11.6, z: 11.6, s: 1.3 },
      { kind: 'treeC', x: -7.5, z: -10.4, s: 1.1 },
      { kind: 'treeC', x: 7.5, z: -10.4, s: 1.1 },
      { kind: 'treeC', x: -7.5, z: 8.6, s: 1.1 },
      { kind: 'treeC', x: 7.5, z: 8.6, s: 1.1 },
      { kind: 'treeB', x: -10.8, z: -1, s: 1.05 },
      { kind: 'treeA', x: 10.8, z: 2, s: 1.1 },
      { kind: 'flower', x: -3.6, z: 7.4 },
      { kind: 'flower', x: -3.6, z: 8.4 },
      { kind: 'flower', x: 3.6, z: 7.9 },
      { kind: 'rock', x: 9.8, z: -7.2, s: 1.3 },
      { kind: 'rock', x: -9.8, z: 5.4, s: 1.1 },
    ],
  },
  {
    name: 'Putaran Penuh',
    desc: 'Ramp naik, selipkan chicane, ramp turun.',
    boxes: [
      [0, 1.5, 0.5, 4, 1.0, 3, PLAT],
      [1, 2.5, 0.5, 0.5, 1.0, 0.5, PLAT],
    ],
    ramps: [
      [0, -4, 3, 6, 0, 2, RAMP],
      [0, 5, 3, 6, 2, 0, RAMP],
    ],
    crates: [{ x: 0, z: -8, size: 1.0, color: 0xbf616a }],
    spawns: [[-1.5, -8], [1.5, -8], [-1.5, -10], [1.5, -10]],
    finish: { x: 0, z: 9.5, r: 1.2, needCrate: true },
    mpExtraCrates: [
      { x: -5, z: -6, size: 1.0, color: 0xbf616a },
      { x: 5, z: -6, size: 1.0, color: 0xbf616a },
    ],
    theme: 'loop',
    arena: { half: 16 },
    deco: [
      { kind: 'treeB', x: 0, z: 13.8, s: 1.5 },
      { kind: 'treeA', x: -3.5, z: 13.6, s: 1.3 },
      { kind: 'treeA', x: 3.5, z: 13.6, s: 1.3 },
      { kind: 'treeC', x: -7, z: 12.6, s: 1.1 },
      { kind: 'treeC', x: 7, z: 12.6, s: 1.1 },
      { kind: 'flower', x: 0, z: 12.2 },
      { kind: 'flower', x: 2.2, z: 12.8 },
      { kind: 'flower', x: -2.2, z: 12.8 },
      { kind: 'flower', x: 5.5, z: 12.9 },
      { kind: 'flower', x: -5.5, z: 12.9 },
      { kind: 'flower', x: 0, z: 14.8 },
      { kind: 'treeC', x: -8, z: -14.8, s: 1.15 },
      { kind: 'treeC', x: 8, z: -14.8, s: 1.15 },
      { kind: 'treeC', x: 0, z: -14.7, s: 1.1 },
      { kind: 'treeA', x: -14.6, z: -14.6, s: 1.3 },
      { kind: 'treeB', x: 14.6, z: -14.6, s: 1.25 },
      { kind: 'treeB', x: -14.6, z: 14.6, s: 1.25 },
      { kind: 'treeA', x: 14.6, z: 14.6, s: 1.35 },
      { kind: 'treeB', x: -12.8, z: 0, s: 1.1 },
      { kind: 'treeA', x: 12.8, z: 0, s: 1.1 },
      { kind: 'treeC', x: -12.8, z: -4, s: 1.08 },
      { kind: 'treeB', x: 12.8, z: 5, s: 1.12 },
      { kind: 'rock', x: 6, z: 11.4, s: 1.25 },
      { kind: 'rock', x: -6, z: 11.4, s: 1.3 },
    ],
  },
  {
    name: 'Jembatan Geser',
    desc: 'Naiki platform geser, lalu selipkan crate lewat tikungan.',
    boxes: [
      [0, 1.3, -4.8, 3, 1.4, 1.6, STEP], // ledge A
      [0, 1.3, 3.4, 3, 1.4, 1.6, STEP], //  ledge B
      [0.2, 0.6, 8.6, 2.4, 1.2, 0.5, PLAT], // koridor: dinding utara seg 1
      [3.95, 0.6, 8.6, 1.9, 1.2, 0.5, PLAT], // dinding utara seg 2 (pintu 1.4..3.0)
      [1.6, 0.6, 6.2, 5, 1.2, 0.5, PLAT], // dinding selatan lane 1
      [5.2, 0.6, 7.2, 0.5, 1.2, 2.2, PLAT], // tutup ujung timur
      [2.2, 0.6, 10.4, 0.5, 1.2, 2.8, PLAT], // dinding barat lane 2
      [5.6, 0.6, 10.2, 0.5, 1.2, 1.4, PLAT], // dinding timur lane 2
    ],
    ramps: [[0, -6.8, 3, 2.8, 0, 1.4, RAMP]],
    crates: [{ x: 0, z: 7.2, size: 1.0, color: 0xf2e3a0 }],
    spawns: [[-1.8, -8], [1.8, -8], [-1.8, -10], [1.8, -10]],
    finish: { x: 4.6, z: 12.2, r: 1.1, needCrate: true },
    mpExtraCrates: [
      { x: -5, z: 8, size: 1.0, color: 0xf2e3a0 },
      { x: 5.5, z: 3, size: 1.0, color: 0xf2e3a0 },
    ],
    platforms: [
      { x: 0, z: -1, w: 3, d: 3, h: 0.7, y: 2.0, axis: 'z', span: 4.6, period: 6, phase: 0.5 },
    ],
    theme: 'glacier',
    arena: { half: 18 },
    deco: [
      { kind: 'treeC', x: -7, z: -16, s: 1.2 },
      { kind: 'treeC', x: 7, z: -16, s: 1.2 },
      { kind: 'treeA', x: -16.5, z: -16.5, s: 1.3 },
      { kind: 'treeB', x: 16.5, z: -16.5, s: 1.3 },
      { kind: 'treeB', x: -16.5, z: 16.5, s: 1.3 },
      { kind: 'treeA', x: 16.5, z: 16.5, s: 1.3 },
      { kind: 'treeC', x: -8.5, z: -2, s: 1.1 },
      { kind: 'treeC', x: 8.5, z: -2, s: 1.1 },
      { kind: 'treeB', x: -10, z: 4, s: 1.15 },
      { kind: 'treeA', x: 10, z: 3, s: 1.15 },
      { kind: 'flower', x: 4.6, z: 13.4 },
      { kind: 'flower', x: 7, z: 12.2 },
      { kind: 'flower', x: 2, z: 13 },
      { kind: 'flower', x: -4.6, z: 13.2 },
      { kind: 'rock', x: -12, z: 8.5, s: 1.2 },
      { kind: 'rock', x: 12, z: -7, s: 1.25 },
    ],
  },
  {
    name: 'Jalur Terburu-buru',
    desc: 'Dorong crate sejauh lari, seberangi jembatan geser (waktunya pas).',
    boxes: [
      [-2.1, 0.6, -7, 0.5, 1.2, 10, PLAT], // dinding barat jalur lari
      [2.1, 0.6, -7, 0.5, 1.2, 10, PLAT], //  dinding timur jalur lari
      [0, 0.25, 10.2, 3, 0.5, 1.6, STEP], // dudukan pad finish
    ],
    ramps: [
      [0, -2.4, 3, 2.2, 0, 1.4, RAMP], // naik ke jembatan
      [0, 4.6, 3, 2.4, 1.4, 0, RAMP], //  turun dari jembatan
    ],
    crates: [{ x: 0, z: -10, size: 1.0, color: 0xf2e3a0 }],
    spawns: [[-1.8, -12], [1.8, -12], [-1.8, -13.6], [1.8, -13.6]],
    finish: { x: 0, z: 10.2, r: 1.1, needCrate: true },
    mpExtraCrates: [
      { x: -4, z: -8, size: 1.0, color: 0xf2e3a0 },
      { x: 4, z: -8, size: 1.0, color: 0xf2e3a0 },
    ],
    platforms: [
      { x: 0, z: 0.4, w: 3, d: 3, h: 0.7, y: 1.4, axis: 'z', span: 5.0, period: 7, phase: 0.5 },
    ],
    theme: 'dash',
    arena: { half: 18 },
    deco: [
      { kind: 'treeC', x: -6, z: -7, s: 1.1 },
      { kind: 'treeC', x: 6, z: -7, s: 1.1 },
      { kind: 'treeC', x: -6, z: 2, s: 1.1 },
      { kind: 'treeC', x: 6, z: 2, s: 1.1 },
      { kind: 'treeA', x: -15.5, z: -15.5, s: 1.25 },
      { kind: 'treeB', x: 15.5, z: -15.5, s: 1.25 },
      { kind: 'treeB', x: -15.5, z: 15.5, s: 1.25 },
      { kind: 'treeA', x: 15.5, z: 15.5, s: 1.25 },
      { kind: 'treeB', x: -10, z: 8, s: 1.1 },
      { kind: 'treeA', x: 10, z: 8, s: 1.1 },
      { kind: 'flower', x: 0, z: 11.6 },
      { kind: 'flower', x: -1.8, z: 11.2 },
      { kind: 'flower', x: 1.8, z: 11.2 },
      { kind: 'flower', x: -6, z: 11.6 },
      { kind: 'flower', x: 6, z: 11.6 },
      { kind: 'rock', x: -8, z: -12, s: 1.2 },
      { kind: 'rock', x: 8, z: -12, s: 1.2 },
    ],
  },
  {
    name: 'Taman Raya',
    desc: 'Grand finale: dua rute, dua crate, dua pad finish. Semua harus terpasang.',
    boxes: [
      [-8.5, 1.4, 1.2, 3.4, 1.2, 2.4, PLAT], // panggung rute A (top 2.0)
      [-8.5, 1.6, 8.0, 3.4, 0.8, 1.6, STEP], // pulau finish A (top 2.0, rata dgn ferry)
      [7.2, 1.0, -4.5, 0.5, 2.0, 0.5, PLAT], // tiang slalom rute B
      [9.8, 1.0, -2.2, 0.5, 2.0, 0.5, PLAT],
      [7.2, 1.0, -1.2, 0.5, 2.0, 0.5, PLAT],
      [8.5, 1.6, 3.6, 3, 0.8, 2.2, STEP], // dataran B (top 2.0)
      [8.5, 1.6, 11.2, 3.2, 0.8, 2.2, STEP], // teras finish B (top 2.0)
    ],
    ramps: [
      [-8.5, -2.5, 2.6, 5.0, 0, 2.0, RAMP], // tanjakan A: puncak 2.0 PERSIS di tepi panggung
      [8.5, 0.75, 2.6, 3.5, 0, 2.0, RAMP], // ramp naik B: puncak 2.0 PERSIS di tepi dataran
    ],
    crates: [
      { x: -8.5, z: -7, size: 1.0, color: 0xf78f8f },
      { x: 8.5, z: -7, size: 1.0, color: 0xf2b8a0 },
    ],
    spawns: [[-1, -8], [1, -8], [-2, -11], [2, -11]],
    finish: { x: -8.5, z: 8.0, r: 1.1, needCrate: true },
    mpExtraCrates: [
      { x: -4.5, z: -9.5, size: 1.0, color: 0xf78f8f },
      { x: 4.5, z: -9.5, size: 1.0, color: 0xf2b8a0 },
    ],
    finishes: [
      { x: -8.5, z: 8.0, r: 1.1, needCrate: true },
      { x: 8.5, z: 12.2, r: 1.0, needCrate: true },
    ],
    platforms: [
      { x: -8.5, z: 4.6, w: 3, d: 2.8, h: 0.7, y: 2.0, axis: 'z', span: 3.6, period: 5.5, phase: 0.5 },
      { x: 8.5, z: 7.5, w: 2.8, d: 2.4, h: 0.6, y: 2.0, axis: 'z', span: 4.6, period: 6.5, phase: 0.5 },
    ],
    theme: 'garden',
    arena: { half: 24 },
    deco: [
      { kind: 'treeA', x: -21, z: -21, s: 1.35 },
      { kind: 'treeB', x: 21, z: -21, s: 1.35 },
      { kind: 'treeB', x: -21, z: 21, s: 1.35 },
      { kind: 'treeA', x: 21, z: 21, s: 1.35 },
      { kind: 'treeC', x: -14, z: -18, s: 1.1 },
      { kind: 'treeC', x: 14, z: -18, s: 1.1 },
      { kind: 'treeC', x: -18, z: -12, s: 1.1 },
      { kind: 'treeC', x: 18, z: -12, s: 1.1 },
      { kind: 'treeB', x: -15, z: 12, s: 1.2 },
      { kind: 'treeA', x: 15, z: 12, s: 1.2 },
      { kind: 'treeA', x: 0, z: 18, s: 1.4 },
      { kind: 'flower', x: 0, z: 10.5 },
      { kind: 'flower', x: -3, z: 11.8 },
      { kind: 'flower', x: 3, z: 11.8 },
      { kind: 'flower', x: -5.5, z: 10.4 },
      { kind: 'flower', x: 5.5, z: 10.4 },
      { kind: 'flower', x: 0, z: 13.4 },
      { kind: 'flower', x: -8.5, z: 9.6 },
      { kind: 'flower', x: 8.5, z: 14 },
      { kind: 'rock', x: -16, z: 2, s: 1.3 },
      { kind: 'rock', x: 16, z: 0, s: 1.3 },
      { kind: 'rock', x: -13, z: -4, s: 1.2 },
      { kind: 'rock', x: 13, z: -4, s: 1.2 },
    ],
  },
  {
    name: 'Sungai Menyeberang',
    desc: 'Sungai dalam menghadang — jatuhkan crate sebagai pijakan lalu lompat menyeberang.',
    boxes: [],
    ramps: [],
    crates: [
      { x: 0, z: -6, size: 1.5, height: 0.8, color: 0xf2e3a0 },
      { x: 0, z: 2, size: 1.5, height: 0.8, color: 0xf2e3a0 },
    ],
    spawns: [[-1.8, -12], [1.8, -12], [-1.8, -13.6], [1.8, -13.6]],
    finish: { x: 0, z: 8, r: 1.2, needCrate: false },
    mpExtraCrates: [
      { x: -4, z: -9, size: 1.0, height: 0.8, color: 0xf5c9a6 },
      { x: 4, z: -9, size: 1.0, height: 0.8, color: 0xf5c9a6 },
    ],
    waters: [{ x: 0, z: 1, w: 36, d: 6, y: -0.35, bed: -0.8 }],
    theme: 'river',
    arena: { half: 18 },
    deco: [
      { kind: 'treeA', x: -16, z: -16, s: 1.3 },
      { kind: 'treeB', x: 16, z: -16, s: 1.3 },
      { kind: 'treeB', x: -16, z: 16, s: 1.3 },
      { kind: 'treeA', x: 16, z: 16, s: 1.3 },
      { kind: 'treeC', x: -7, z: -14.5, s: 1.1 },
      { kind: 'treeC', x: 7, z: -14.5, s: 1.1 },
      { kind: 'treeC', x: -12, z: -3.2, s: 1.1 },
      { kind: 'treeC', x: 12, z: -3.2, s: 1.1 },
      { kind: 'treeC', x: -12, z: 5.2, s: 1.1 },
      { kind: 'treeC', x: 12, z: 5.2, s: 1.1 },
      { kind: 'treeB', x: -11, z: 11, s: 1.15 },
      { kind: 'treeA', x: 11, z: 11, s: 1.15 },
      { kind: 'flower', x: -6, z: -3.2 },
      { kind: 'flower', x: 6, z: -3.2 },
      { kind: 'flower', x: -6, z: 5.2 },
      { kind: 'flower', x: 6, z: 5.2 },
      { kind: 'flower', x: -2.5, z: 9.5 },
      { kind: 'flower', x: 2.5, z: 9.5 },
      { kind: 'rock', x: -9, z: 7.5, s: 1.2 },
      { kind: 'rock', x: 9, z: 7.5, s: 1.25 },
      { kind: 'rock', x: -13, z: -6, s: 1.15 },
      { kind: 'rock', x: 13, z: -6, s: 1.15 },
    ],
  },
];

export function stageCountCheck(): boolean {
  return STAGES.length === STAGE_COUNT;
}

const BEST_KEY = 'slime-brawler:best-times';

export function loadBest(index: number): number | null {
  try {
    const raw = window.localStorage.getItem(BEST_KEY);
    if (!raw) return null;
    const v = (JSON.parse(raw) as Record<string, unknown>)[String(index)];
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export function saveBest(index: number, seconds: number): boolean {
  try {
    const raw = window.localStorage.getItem(BEST_KEY);
    const all = (raw ? JSON.parse(raw) : {}) as Record<string, number>;
    const prev = all[String(index)];
    if (typeof prev === 'number' && prev <= seconds) return false;
    all[String(index)] = seconds;
    window.localStorage.setItem(BEST_KEY, JSON.stringify(all));
    return true;
  } catch {
    return false;
  }
}

/** m:ss.t */
export function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}
